import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Dumbbell,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOLD_GRADIENT = 'linear-gradient(135deg, #FFD700 0%, #F59E0B 100%)';
const DARK_GRADIENT = 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)';

const BrandMark = ({ compact = false }) => (
  <div className="flex items-center gap-3" data-testid={compact ? 'verify-mobile-brand' : 'verify-desktop-brand'}>
    <div
      className={`${compact ? 'h-10 w-10' : 'h-14 w-14'} flex shrink-0 items-center justify-center rounded-lg`}
      style={{ background: GOLD_GRADIENT, boxShadow: '0 0 28px rgba(255, 215, 0, 0.18)' }}
    >
      <Dumbbell className="text-black" size={compact ? 20 : 28} />
    </div>
    <span
      className={`${compact ? 'text-2xl' : 'text-5xl'} font-extrabold text-white`}
      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
    >
      STRONG<span className="text-yellow-400">FIT</span>
    </span>
  </div>
);

const VerifyCustomerPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const signatureRef = useRef();
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  const clearSignature = () => signatureRef.current?.clear();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!agbAccepted) {
      toast.error('Bitte akzeptieren Sie die AGB');
      return;
    }
    if (signatureRef.current?.isEmpty()) {
      toast.error('Bitte unterschreiben Sie den Haftungsausschluss');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/customers/verify/${token}`, {
        agb_accepted: agbAccepted,
        signature_data: signatureRef.current.toDataURL(),
      });
      setVerified(true);
      toast.success('Verifizierung erfolgreich!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verifizierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  if (verified) {
    return (
      <main
        className="min-h-screen bg-[#0a0a0a] px-4 py-10 text-white sm:px-8"
        data-testid="verify-customer-page"
      >
        <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-xl items-center justify-center">
          <Card
            className="w-full rounded-lg border-[#2a2a2a] bg-[#1a1a1a] text-center text-white shadow-2xl"
            data-testid="verification-success-card"
          >
            <CardContent className="px-6 py-12 sm:px-10">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-yellow-400/30 bg-yellow-400/10">
                <CheckCircle2 className="text-yellow-400" size={36} />
              </div>
              <h1 className="text-4xl font-extrabold sm:text-5xl">Verifizierung erfolgreich</h1>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400 sm:text-base" data-testid="verification-success-message">
                Ihr StrongFit-Konto ist bereit. Sie werden gleich zur Anmeldung weitergeleitet.
              </p>
              <div className="mx-auto mt-8 h-1 w-24 overflow-hidden rounded-full bg-zinc-800">
                <div className="h-full w-full animate-pulse bg-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen overflow-x-hidden bg-[#0a0a0a] text-white lg:flex"
      data-testid="verify-customer-page"
    >
      <section
        className="relative hidden w-1/2 items-center justify-center overflow-hidden p-12 lg:flex"
        style={{ background: DARK_GRADIENT }}
      >
        <div
          className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-[38rem] -translate-x-1/2 opacity-70"
          style={{ background: 'radial-gradient(ellipse, rgba(255, 215, 0, 0.2), transparent 70%)' }}
        />
        <div className="relative z-10 max-w-md animate-fade-up">
          <BrandMark />
          <h1 className="mt-10 text-4xl font-extrabold leading-none sm:text-5xl">
            Fast geschafft.<br />
            <span className="text-yellow-400">Konto aktivieren.</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-400">
            Bestätigen Sie die rechtlichen Grundlagen und hinterlegen Sie Ihre Unterschrift sicher.
          </p>
          <div className="mt-10 space-y-4">
            {[
              'AGB und Datenschutz transparent bestätigt',
              'Haftungsausschluss digital unterschrieben',
              'Direkter Zugang zum StrongFit Kundenportal',
            ].map((text) => (
              <div key={text} className="flex items-center gap-3 text-sm text-zinc-300">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-yellow-400/25 bg-yellow-400/10">
                  <Check className="text-yellow-400" size={16} />
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="flex min-h-screen w-full items-center justify-center px-4 py-8 sm:px-8 lg:w-1/2 lg:py-12">
        <div className="w-full max-w-lg">
          <div className="mb-8 lg:hidden">
            <BrandMark compact />
          </div>

          <Card
            className="w-full rounded-lg border-[#2a2a2a] bg-[#1a1a1a] text-white shadow-2xl animate-fade-up"
            data-testid="verify-card"
          >
            <CardHeader className="space-y-2 border-b border-[#2a2a2a] px-5 pb-5 pt-6 sm:px-8 sm:pt-8">
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg border border-yellow-400/25 bg-yellow-400/10">
                <ShieldCheck className="text-yellow-400" size={21} />
              </div>
              <CardTitle className="text-3xl font-extrabold text-white sm:text-4xl">
                Konto verifizieren
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed text-zinc-400 sm:text-base">
                Schliessen Sie Ihre Registrierung sicher ab.
              </CardDescription>
            </CardHeader>

            <CardContent className="px-5 py-6 sm:px-8 sm:py-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="verification-form">
                <div className="flex items-start gap-3 rounded-lg border border-[#2a2a2a] bg-[#121212] p-4">
                  <Checkbox
                    id="agb"
                    checked={agbAccepted}
                    onCheckedChange={setAgbAccepted}
                    className="mt-0.5 h-5 w-5 border-zinc-600 data-[state=checked]:border-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:text-black"
                    data-testid="agb-checkbox"
                  />
                  <label htmlFor="agb" className="cursor-pointer text-sm leading-relaxed text-zinc-300">
                    Ich akzeptiere die{' '}
                    <a
                      href="/agb"
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-yellow-400 transition-colors hover:text-yellow-300 hover:underline"
                      data-testid="agb-link"
                    >
                      Allgemeinen Geschäftsbedingungen
                    </a>{' '}
                    und die{' '}
                    <a
                      href="/datenschutz"
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-yellow-400 transition-colors hover:text-yellow-300 hover:underline"
                      data-testid="privacy-link"
                    >
                      Datenschutzerklärung
                    </a>
                    .
                  </label>
                </div>

                <div className="space-y-3 rounded-lg border border-[#2a2a2a] bg-[#121212] p-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="text-yellow-400" size={17} />
                    <h2 className="text-base font-bold normal-case text-white">Haftungsausschluss</h2>
                  </div>
                  <p className="text-sm leading-relaxed text-zinc-400">
                    Ich bestätige, gesundheitlich in der Lage zu sein, am Training teilzunehmen. Ich befreie das Studio von der Haftung für Verletzungen oder Schäden während oder infolge des Trainings.
                  </p>
                  <div className="overflow-hidden rounded-lg border border-zinc-700 bg-[#0d0d0d]">
                    <SignatureCanvas
                      ref={signatureRef}
                      penColor="#FFD700"
                      backgroundColor="#0d0d0d"
                      canvasProps={{
                        className: 'block h-44 w-full touch-none',
                        'data-testid': 'signature-canvas',
                        'aria-label': 'Unterschriftsfeld',
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearSignature}
                    className="h-10 border-zinc-700 bg-transparent text-zinc-300 transition-colors hover:border-yellow-400 hover:bg-yellow-400/10 hover:text-yellow-300"
                    data-testid="clear-signature-button"
                  >
                    <RotateCcw size={15} />
                    Unterschrift löschen
                  </Button>
                </div>

                <Button
                  type="submit"
                  className="h-12 w-full rounded-lg font-bold uppercase text-black transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
                  style={{ background: GOLD_GRADIENT }}
                  disabled={loading}
                  data-testid="verify-submit-button"
                >
                  {loading ? 'Wird verifiziert...' : 'Verifizierung abschliessen'}
                  {!loading && <ArrowRight size={17} />}
                </Button>

                <p className="text-center text-xs text-zinc-500">
                  Sicherer Abschluss Ihrer StrongFit Registrierung
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </main>
  );
};

export default VerifyCustomerPage;